/**
 * Reports, in the dashboard's visual language: the headline figures in one
 * hairline strip, the period's shape beside where its leads ended up, then the
 * per-campaign and per-buyer tables.
 *
 * The funnel used to lead the page on its own. It now sits beside the trend
 * as proportion rows, the way the dashboard shows its delivery breakdown,
 * with the total received as its subtitle: still the denominator every other
 * figure divides by, stated where the eye reads it.
 *
 * Count: people swaps the received count, the trend and the per-campaign
 * count for distinct people over the same period (`/api/v1/people/counts`).
 * Revenue and the rates stay per submission.
 */
import { useMemo, useState } from 'react';
import { Alert, Grid, Group, Loader, Stack, Table, Tabs, Text } from '@mantine/core';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useReports, type ReportRange } from '../api/queries';
import { usePeopleCounts, type PeopleCounts } from '../api/people';
import { CountToggle, useCountUnit } from './people/CountToggle';
import { TOKENS, formatGBP, formatNumber } from '../theme';
import { FunnelBar, funnelItems } from './LeadFunnel';
import {
  BAR_CURSOR, CHART_INK, GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, chartAnimation, formatCompactNumber,
} from '../charts';
import {
  ChartCard, ChartTooltip, LegendItem, MonoSegmented, PageHeader, ProportionList, SpeedChip, StatGrid,
  StatTile, usePrefersReducedMotion,
} from '../ui';
import { formatCount, formatPercent, formatPounds, isoDayLabel, ukDayLabel } from '../ui/format';
import { ReportsExplore } from './ReportsExplore';
import { ReportsEntities } from './ReportsEntities';
import { useVocab } from '../vocab';
import { ReportsReturns } from './ReportsReturns';
import { useNavUser } from '../layout/navUser';
import { hasAction, isScopedUser } from '../layout/nav';

const RANGES: { value: ReportRange; label: string; bucket: string }[] = [
  { value: 'today', label: 'Today', bucket: 'Today' },
  { value: '7d', label: '7 days', bucket: 'Per day, last 7 days' },
  { value: '30d', label: '30 days', bucket: 'Per day, last 30 days' },
  { value: '90d', label: '90 days', bucket: 'Per day, last 90 days' },
];

const TREND_H = 240;

/** Figures in a table cell, in the same mono as the dashboard's lists. */
const fig = { fontFamily: 'var(--mantine-font-family-monospace)', fontVariantNumeric: 'tabular-nums', fontSize: 12.5 } as const;

export function ReportsPage() {
  const v = useVocab();
  const [range, setRange] = useState<ReportRange>('30d');
  const { data, isPending, error, isPlaceholderData } = useReports({ date_range: range });
  const meta = RANGES.find((r) => r.value === range) ?? RANGES[2]!;
  const count = useCountUnit();
  const peopleCounts = usePeopleCounts({ date_range: range }, count.unit === 'people');
  const people = count.unit === 'people' ? peopleCounts.data : undefined;
  // Returns by supplier, buyer and so on: staff who run returns.
  const navUser = useNavUser();
  const returnsTab = !isScopedUser(navUser) && hasAction(navUser, 'manage_returns');

  return (
    <Stack gap="lg">
      {/* No campaign filter here.

          It scoped Performance and Entities, and it fought the page it sat
          on: the campaign table breaks down by campaign already, with a
          funnel each, so narrowing to one campaign reduced that table to a
          single row and told you less than the unfiltered view.

          Ad-hoc slicing belongs in Explore, which owns its own filters. */}
      <PageHeader
        title={v.p.reports}
        actions={
          <Group gap="md" wrap="wrap">
            {count.available && (
              <CountToggle unit={count.unit} onChange={count.setUnit} updating={people?.updating ?? false} />
            )}
            <MonoSegmented
              value={range}
              onChange={(v) => setRange(v as ReportRange)}
              data={RANGES.map((r) => ({ value: r.value, label: r.label }))}
              aria-label="Period"
            />
          </Group>
        }
      />

      <Tabs defaultValue="performance">
        <Tabs.List mb="md">
          <Tabs.Tab value="performance">Performance</Tabs.Tab>
          <Tabs.Tab value="entities">Entities</Tabs.Tab>
          <Tabs.Tab value="explore">Explore</Tabs.Tab>
          {returnsTab && <Tabs.Tab value="returns">Returns</Tabs.Tab>}
        </Tabs.List>

        {returnsTab && (
          <Tabs.Panel value="returns">
            <ReportsReturns />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="entities">
          <ReportsEntities range={range} />
        </Tabs.Panel>

        {/* Explore owns its own period and grouping, since a saved view has to
            restore both together. */}
        <Tabs.Panel value="explore">
          <ReportsExplore />
        </Tabs.Panel>

        <Tabs.Panel value="performance">
          {error && <Alert color="red" variant="light" mb="md">{error.message}</Alert>}
          {count.unit === 'people' && peopleCounts.error && (
            <Alert color="red" variant="light" mb="md">{peopleCounts.error.message}</Alert>
          )}
          {isPending && <Group justify="center" p="xl"><Loader /></Group>}
          {data && (
            <div className={isPlaceholderData ? 'hf-stale' : 'hf-fresh'} aria-busy={isPlaceholderData}>
              <Performance data={data} bucket={meta.bucket} people={count.unit === 'people' ? people ?? null : undefined} />
              <Group justify="flex-end" mt="md">
                <SpeedChip stats={data.query_stats} />
              </Group>
            </div>
          )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

type Reports = NonNullable<ReturnType<typeof useReports>['data']>;

/**
 * `people` is undefined when counting submissions, null while the people
 * figures load, and the figures once they have.
 */
function Performance({ data, bucket, people }: { data: Reports; bucket: string; people?: PeopleCounts | null }) {
  const m = data.metrics;
  const v = useVocab();
  const byPeople = people !== undefined;
  const spark = byPeople
    ? (people && people.trend.length >= 2 ? people.trend.map((p) => p.people) : undefined)
    : data.lead_trend.length >= 2 ? data.lead_trend.map((p) => p.count) : undefined;
  const streamPeople = useMemo(
    () => new Map((people?.streams ?? []).map((s) => [s.campaign_id, s.people])),
    [people],
  );
  const funnel = useMemo(() => funnelItems(data.status_breakdown.map((d) => ({ status: d.name, count: d.count }))), [data.status_breakdown]);
  // Which money this login may see; the server has already blanked the rest.
  const money = data.money ?? { revenue: true, cost: false, margin: false };
  const ownCost = money.cost && !money.revenue;
  const campaignCols = 4 + (money.revenue ? 2 : 0) + (money.cost ? 1 : 0) + (money.margin ? 1 : 0);

  return (
    <Stack gap="md">
      <StatGrid columns={money.revenue ? 4 : 2}>
        {byPeople ? (
          <StatTile
            index={0}
            label={v.Many('person')}
            value={people?.people ?? 0}
            format={people ? formatCount : () => '…'}
            caption={people ? `From ${v.count(people.submissions, 'lead')}` : 'Counting'}
            spark={spark}
          />
        ) : (
          <StatTile index={0} label={v.Many('lead')} value={m.total_leads} format={formatCount} caption="Received this period" spark={spark} />
        )}
        {money.revenue && (
          <StatTile
            index={1}
            label="Revenue"
            value={m.total_revenue}
            format={formatPounds}
            caption={`From ${v.many('lead')} ${v.p.sold}`}
          />
        )}
        {/*
          Not the campaign table's "per lead received". This divides revenue
          by DELIVERED leads only (£10.91 on Energy Premium, against £4.48 per
          lead received): same campaign, same period, a 2.4x difference, so
          the label says which.
        */}
        {money.revenue && (
          <StatTile
            index={2}
            label={`Per delivered ${v.one('lead')}`}
            value={m.avg_revenue}
            format={formatGBP}
            caption={`Revenue ÷ ${v.many('lead')} delivered`}
          />
        )}
        <StatTile index={3} label="Delivery rate" value={m.delivery_rate} format={formatPercent} meter={m.delivery_rate / 100} />
      </StatGrid>

      {(money.cost || money.margin) && (
        <StatGrid columns={money.margin ? 2 : 1}>
          {money.cost && (
            <StatTile
              index={4}
              label={ownCost ? 'Earned' : 'Cost'}
              value={m.total_payout}
              format={formatPounds}
              caption={ownCost ? `What your ${v.many('lead')} earned` : `Paid to ${v.many('supplier')}, every ${v.one('lead')} counted`}
            />
          )}
          {money.margin && (
            <StatTile
              index={5}
              label="Margin"
              value={m.margin ?? 0}
              format={formatPounds}
              caption={m.margin_pct != null ? `${formatPercent(m.margin_pct)} of revenue` : 'Revenue less cost'}
            />
          )}
        </StatGrid>
      )}

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <TrendCard data={data} subtitle={bucket} people={people} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <ChartCard
            index={5}
            title={`Where these ${v.many('lead')} went`}
            subtitle={`${v.count(funnel.total, 'lead')} received`}
            empty={funnel.total === 0}
            emptyText={`No ${v.many('lead')} in this period`}
          >
            <ProportionList items={funnel.items} />
          </ChartCard>
        </Grid.Col>
      </Grid>

      <ChartCard
        index={6}
        title={`${v.One('campaign')} performance`}
        subtitle={`Per ${v.one('campaign')}, this period`}
        flush
      >
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th pl={20}>{v.One('campaign')}</Table.Th>
                <Table.Th ta="right">{byPeople ? v.Many('person') : v.Many('lead')}</Table.Th>
                {money.revenue && <Table.Th ta="right">Revenue</Table.Th>}
                {money.revenue && <Table.Th ta="right">{`Per ${v.one('lead')} received`}</Table.Th>}
                {money.cost && <Table.Th ta="right">{ownCost ? 'Earned' : 'Cost'}</Table.Th>}
                {money.margin && <Table.Th ta="right">Margin</Table.Th>}
                <Table.Th w={210} pl={32}>Funnel</Table.Th>
                <Table.Th ta="right" pr={20}>Delivered</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.campaigns.length === 0 && (
                <Table.Tr><Table.Td colSpan={campaignCols}>
                  <Text ta="center" c="dimmed" py="lg" size="sm">{`No ${v.one('campaign')} activity in this period.`}</Text>
                </Table.Td></Table.Tr>
              )}
              {data.campaigns.map((c) => (
                <Table.Tr key={c.campaign_id}>
                  <Table.Td pl={20}><Text size="sm" fw={500} lineClamp={1}>{c.campaign_name}</Text></Table.Td>
                  <Table.Td ta="right" style={fig}>
                    {byPeople ? fmtMaybe(streamPeople.get(c.campaign_id), !!people) : formatNumber(c.total_leads)}
                  </Table.Td>
                  {money.revenue && (
                    <Table.Td ta="right" style={{ ...fig, fontWeight: 600, color: TOKENS.text }}>{formatGBP(c.total_revenue)}</Table.Td>
                  )}
                  {money.revenue && <Table.Td ta="right" style={fig}>{formatGBP(c.avg_revenue)}</Table.Td>}
                  {money.cost && <Table.Td ta="right" style={fig}>{formatGBP(c.total_payout)}</Table.Td>}
                  {money.margin && (
                    <Table.Td ta="right" style={{ ...fig, color: (c.margin ?? 0) < 0 ? TOKENS.redText : undefined }}>
                      {c.margin != null ? formatGBP(c.margin) : '—'}
                      {c.margin_pct != null && (
                        <Text span size="xs" c="dimmed">{` ${c.margin_pct.toFixed(1)}%`}</Text>
                      )}
                    </Table.Td>
                  )}
                  <Table.Td pl={32}><FunnelBar counts={c.funnel} /></Table.Td>
                  <Table.Td ta="right" pr={20} style={fig}>{c.delivery_rate.toFixed(1)}%</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </ChartCard>

      <ChartCard
        index={7}
        title={`${v.One('buyer')} performance`}
        subtitle={`Per ${v.one('buyer')}, this period`}
        flush
      >
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th pl={20}>{v.One('buyer')}</Table.Th>
                <Table.Th ta="right">Attempted</Table.Th>
                <Table.Th ta="right">Delivered</Table.Th>
                <Table.Th ta="right">Failed</Table.Th>
                {money.revenue && <Table.Th ta="right">Avg price</Table.Th>}
                {money.revenue && <Table.Th ta="right">Revenue</Table.Th>}
                <Table.Th ta="right" pr={20}>Success</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.buyers.length === 0 && (
                <Table.Tr><Table.Td colSpan={money.revenue ? 7 : 5}>
                  <Text ta="center" c="dimmed" py="lg" size="sm">{`No ${v.one('buyer')} activity in this period.`}</Text>
                </Table.Td></Table.Tr>
              )}
              {data.buyers.map((b) => (
                <Table.Tr key={b.buyer_name}>
                  <Table.Td pl={20}><Text size="sm" fw={500}>{b.buyer_name || '—'}</Text></Table.Td>
                  <Table.Td ta="right" style={fig}>{formatNumber(b.total_leads)}</Table.Td>
                  <Table.Td ta="right" style={fig}>{formatNumber(b.delivered)}</Table.Td>
                  <Table.Td ta="right" style={{ ...fig, color: b.failed > 0 ? TOKENS.redText : undefined }}>
                    {formatNumber(b.failed)}
                  </Table.Td>
                  {money.revenue && <Table.Td ta="right" style={fig}>{formatGBP(b.price)}</Table.Td>}
                  {money.revenue && (
                    <Table.Td ta="right" style={{ ...fig, fontWeight: 600, color: TOKENS.text }}>{formatGBP(b.total_revenue)}</Table.Td>
                  )}
                  <Table.Td ta="right" pr={20} style={fig}>{b.success_rate.toFixed(1)}%</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </ChartCard>
    </Stack>
  );
}

/**
 * Leads per day as bars. The server sends only days that had leads, and a
 * line through one or two points draws a lone dot or a misleading slope;
 * a bar per day reads correctly at any count.
 */
function TrendCard({ data, subtitle, people }: { data: Reports; subtitle: string; people?: PeopleCounts | null }) {
  const reduced = usePrefersReducedMotion();
  const byPeople = people !== undefined;
  const rows = useMemo(
    () => (byPeople
      ? (people?.trend ?? []).map((p) => ({ label: isoDayLabel(p.date.slice(0, 10)), count: p.people }))
      : data.lead_trend.map((p) => ({ label: ukDayLabel(p.date), count: p.count }))),
    [data.lead_trend, people, byPeople],
  );
  const v = useVocab();
  // People in the period are not the sum of people per day: one person on
  // two days is one person. The total is the server's.
  const total = byPeople ? (people?.people ?? 0) : rows.reduce((n, r) => n + r.count, 0);
  const noun = byPeople ? v.Many('person') : v.Many('lead');

  return (
    <ChartCard
      index={4}
      title={`${noun} over time`}
      subtitle={subtitle}
      bodyHeight={TREND_H}
      empty={total === 0 && rows.every((r) => r.count === 0)}
      emptyText={`No ${v.many('lead')} in this period`}
      right={<LegendItem color={CHART_INK} label={noun} value={formatNumber(total)} />}
    >
      <div role="img" aria-label={`${noun} over time, ${subtitle.toLowerCase()}`}>
        <ResponsiveContainer width="100%" height={TREND_H}>
          <BarChart data={rows} margin={{ top: 6, right: 0, bottom: 0, left: -4 }} barCategoryGap="22%">
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="label" {...X_AXIS_PROPS} />
            <YAxis {...Y_AXIS_PROPS} allowDecimals={false} tickFormatter={formatCompactNumber} />
            <Tooltip
              cursor={BAR_CURSOR}
              isAnimationActive={false}
              content={
                <ChartTooltip names={{ count: noun }} colors={{ count: CHART_INK }} formatters={{ count: formatNumber }} />
              }
            />
            <Bar dataKey="count" name={noun} fill={CHART_INK} fillOpacity={0.85} radius={[3, 3, 0, 0]} maxBarSize={28} {...chartAnimation(reduced)} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

/** A count that may not have arrived: an ellipsis while it loads, 0 when the server has none. */
function fmtMaybe(n: number | undefined, loaded: boolean): string {
  if (!loaded) return '…';
  return formatNumber(n ?? 0);
}
